import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { debounce } from "throttle-debounce";
import { format, parseISO } from "date-fns";
import styles from "@/components/LanguageModelArtifact/style.module.css";
import SearchInput from "@/components/Inputs/SearchInput/SearchInput";
import { isEnabled } from "@/utils/featureFlags";
import { Button } from "@/aible";
import storefrontIcon from "images/store-sharp-purple.svg";
import sparklesPurpleImg from "images/admin_images/sparkles-purple.svg";
import filesImg from "images/files.svg";
import { useSelector } from "@/types/store";
import * as foldersReducer from "@/containers/Folders/reducer";
import * as contentGenerationReducer from "@/containers/ContentGeneration/reducer";
import CreateTrainingDataModal from "@/components/LanguageModelArtifact/TrainingData/CreateTrainingDataModal";
import CreateCustomTrainingDataModal from "@/components/LanguageModelArtifact/TrainingData/CreateCustomTrainingDataModal";
import TrainingDataTable from "@/components/LanguageModelArtifact/TrainingData/TrainingDataTable";
import { FEEDBACK_BREAKPOINTS } from "@/containers/LanguageModelArtifact/reducer";
import FineTuneTrainingModal from "@/components/LanguageModelArtifact/FineTuneTrainingModal";
import ModelEvaluationModal from "@/components/LanguageModelArtifact/ModelEvaluationModal";
import EditExampleSetModal from "@/components/LanguageModelArtifact/TrainingData/EditExampleSetModal";
export var ModelEvaluationStatus;
(function (ModelEvaluationStatus) {
    ModelEvaluationStatus["NOT_READY"] = "Not Ready";
    ModelEvaluationStatus["MIN"] = "Ok to Improve (more data recommended)";
    ModelEvaluationStatus["RECOMMENDED"] = "Ready to Improve AI";
})(ModelEvaluationStatus || (ModelEvaluationStatus = {}));
const getStatus = (totalFeedback) => {
    if (totalFeedback >= FEEDBACK_BREAKPOINTS.RECOMMENDED) {
        return ModelEvaluationStatus.RECOMMENDED;
    }
    if (totalFeedback >= FEEDBACK_BREAKPOINTS.MIN) {
        return ModelEvaluationStatus.MIN;
    }
    return ModelEvaluationStatus.NOT_READY;
};
const TrainingData = ({ downloadArtifactFeedback }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const [showCreateDatasetModal, updateShowCreateDatasetModal] = useState(false);
    const [showCreateCustomDatasetModal, updateShowCreateCustomDatasetModal] = useState(false);
    const [searchFilter, updateSearchFilter] = useState("");
    const [createDataView, updateCreateDataView] = useState("reports");
    const [showTrainingModal, updateShowTrainingModal] = useState(false);
    const [selectedDataset, updateSelectedDataset] = useState(undefined);
    const [selectedModel, updateSelectedModel] = useState(undefined);
    const [showModelEvaluation, setShowModelEvaluation] = useState(false);
    const [selectedDatasetEvaluation, updateSelectedDatasetEvaluation] = useState(undefined);
    const [showExampleSetModal, updateShowExampleSetModal] = useState(false);
    const [selectedExampleSetId, updateSelectedExampleSetId] = useState(undefined);
    const debouncedSearchFilter = debounce(200, updateSearchFilter);
    const selectedFolderId = useSelector((state) => foldersReducer.getSelectedFolder(state));
    const exampleSetSelector = useSelector((state) => contentGenerationReducer.getExampleSets(state));
    const exampleSets = contentGenerationReducer.filterExampleSets(exampleSetSelector, selectedFolderId);
    const dataTableTitle = exampleSets?.length === 0
        ? "0 Training Datasets Available. Choose one of the options above to add your first fine-tuning dataset."
        : exampleSets?.length === 1
            ? `${exampleSets?.length} Training Dataset Available`
            : `${exampleSets?.length} Training Datasets Available`;
    const handleModelEvaluationAction = (datasetId) => {
        updateSelectedDatasetEvaluation(datasetId);
        setShowModelEvaluation(true);
    };
    const handleDeleteTrainingDataset = (id) => {
        const dataset = exampleSets.find((d) => d?.id === id);
        if (dataset !== undefined) {
            dispatch(contentGenerationReducer.deleteExampleSet(dataset?.id));
        }
    };
    const trainingDataInfo = exampleSets
        ?.map((exampleSet) => {
        const totalFeedback = exampleSet?.result_count ?? 0;
        const createdDate = parseISO(exampleSet?.created_date + "Z");
        const formattedDate = format(createdDate, "MM/dd/yyyy, hh:mm aa");
        const status = getStatus(totalFeedback);
        return {
            name: exampleSet?.name,
            status: status,
            totalFeedback: totalFeedback ?? 0,
            totalProgress: totalFeedback ?? 0,
            owner: exampleSet?.created_by?.fullname,
            createdDate: exampleSet?.created_date && formattedDate,
            id: exampleSet?.id,
            value: exampleSet?.id,
            label: exampleSet?.name,
            exampleSet: exampleSet
        };
    })
        .reverse();
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.fineTuningModelActions },
            React.createElement("div", { className: styles.fineTuningModelActionsContainer },
                isEnabled("community-market") && (React.createElement(Button, { onClick: () => {
                        history.push("/community_market");
                    }, style: { marginRight: "10px" } },
                    React.createElement("img", { className: styles.buttonIcon, src: storefrontIcon }),
                    "Search Marketplace")),
                isEnabled("create-training-data-from-use-case") && (React.createElement(Button, { onClick: () => {
                        updateShowCreateDatasetModal(true);
                    }, style: { marginRight: "10px" } },
                    React.createElement("img", { className: styles.buttonIcon, src: sparklesPurpleImg }),
                    "Create Dataset from User Feedback")),
                React.createElement(Button, { onClick: () => {
                        updateShowCreateCustomDatasetModal(true);
                    } },
                    React.createElement("img", { style: { height: "20px" }, className: styles.buttonIcon, src: filesImg }),
                    "Upload Custom Data"))),
        React.createElement("div", { className: styles.fineTuningDataPanel },
            React.createElement("div", { className: styles.fineTuningTableHeader },
                React.createElement("div", { className: styles.fineTuningTableTitle }, dataTableTitle),
                exampleSets?.length > 0 && (React.createElement("div", { className: styles.fineTuningActions },
                    React.createElement(SearchInput, { value: searchFilter, onChange: (val) => debouncedSearchFilter(val), style: { height: "37px" } })))),
            exampleSets?.length > 0 && (React.createElement("div", { className: styles.fineTuningTableContainer },
                React.createElement(TrainingDataTable, { data: trainingDataInfo, searchFilter: searchFilter, downloadArtifactFeedback: downloadArtifactFeedback, updateShowModal: updateShowTrainingModal, updateSelectedDataset: updateSelectedDataset, updateSelectedModel: updateSelectedModel, handleDelete: handleDeleteTrainingDataset, handleModelEvaluationAction: handleModelEvaluationAction, updateShowExampleSetModal: updateShowExampleSetModal, updateSelectedExampleSetId: updateSelectedExampleSetId })))),
        isEnabled("create-training-data-from-use-case") && (React.createElement(CreateTrainingDataModal, { showModal: showCreateDatasetModal, updateShowModal: updateShowCreateDatasetModal, currentView: createDataView, updateCurrentView: updateCreateDataView })),
        isEnabled("edit-example-sets") && (React.createElement(EditExampleSetModal, { showModal: showExampleSetModal, updateShowModal: updateShowExampleSetModal, selectedExampleSetId: selectedExampleSetId, updateSelectedExampleSetId: updateSelectedExampleSetId })),
        React.createElement(CreateCustomTrainingDataModal, { showModal: showCreateCustomDatasetModal, updateShowModal: updateShowCreateCustomDatasetModal }),
        React.createElement(FineTuneTrainingModal, { showModal: showTrainingModal, updateShowModal: updateShowTrainingModal, dataset: selectedDataset, updateSelectedDataset: updateSelectedDataset, datasetOptions: exampleSets, selectedModel: selectedModel, updateSelectedModel: updateSelectedModel }),
        React.createElement(ModelEvaluationModal, { open: showModelEvaluation, onOpenChange: setShowModelEvaluation, defaultDatasetId: selectedDatasetEvaluation })));
};
export default TrainingData;
